import { Link } from "react-router-dom";
import { PublicLayout } from "@/landing/PublicLayout";
import { Offers } from "@/landing/Offers";
import { DemoResults } from "@/landing/DemoResults";

export function LandingPage() {
  return (
    <PublicLayout>
      <section className="sc-container sc-hero">
        <div className="sc-hero-copy">
          <p className="sc-eyebrow">Scoring · Broadcast · Public results</p>
          <h1>A clear view of competition.</h1>
          <p className="sc-lead">
            Judge, broadcast and publish wakeboard, wakesurf and cable events in any browser.
          </p>
          <div className="sc-actions">
            <Link className="sc-button sc-button-primary" to="/login">
              Start free <span aria-hidden="true">→</span>
            </Link>
            <Link className="sc-button" to="/results/demo">
              Try demo
            </Link>
          </div>
          <p className="sc-secondary">
            No account needed for the demo. Two small events a year free.
          </p>
        </div>
        <div className="sc-preview">
          <div className="sc-preview-bar">
            <span>scorio.live / results / demo</span>
            <span className="sc-tag">Product demo</span>
          </div>
          <DemoResults compact />
        </div>
      </section>
      <section className="sc-container sc-section" id="product">
        <div className="sc-section-heading">
          <p className="sc-eyebrow">From the first score to the final result</p>
          <h2>One event. A shared view.</h2>
          <p>Give each role a focused workspace and keep the audience on the published result.</p>
        </div>
        <div className="sc-three">
          {[
            {
              step: "01",
              title: "Set up your competition",
              body: "Create heats, assign riders and choose the scoring model. Keep your team’s roles separate.",
            },
            {
              step: "02",
              title: "Judge in the browser",
              body: "A dedicated judging pad on phones, tablets and computers. Scores follow your competition’s configuration.",
            },
            {
              step: "03",
              title: "Broadcast and publish",
              body: "Control the OBS overlay, share public results and show the event on a venue screen.",
            },
          ].map((item) => (
            <article className="sc-panel" key={item.step}>
              <span className="sc-step">{item.step}</span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="sc-container sc-section sc-split">
        <div>
          <p className="sc-eyebrow">Built for the people running the day</p>
          <h2>Focus on your role.</h2>
          <p>
            Judges score. Producers choose what is on air. Organisers manage the competition. Public
            screens receive the audience view.
          </p>
          <Link className="sc-text-link" to="/training">
            Explore training and certificates →
          </Link>
        </div>
        <div className="sc-panel">
          <h3>A broadcast that belongs to your event</h3>
          <p>
            Browser-source graphics for OBS, public live results, TV mode and embeddable results.
            Sponsor placements are available with an Event pass or Club Unlimited.
          </p>
          <Link className="sc-text-link" to="/training/producer">
            OBS setup and support →
          </Link>
        </div>
      </section>
      <section className="sc-container sc-section">
        <div className="sc-section-heading">
          <p className="sc-eyebrow">Room to start. Room to grow.</p>
          <h2>Two small events a year free.</h2>
          <p>
            Start with live judging and broadcast. Paid offers below describe the current public and
            broadcast scope.
          </p>
        </div>
        <Offers />
        <p className="sc-notice">
          Paid plans are not on sale yet. You can create a free account and explore the demo.
        </p>
        <Link className="sc-text-link" to="/pricing">
          See pricing and what’s planned →
        </Link>
      </section>
      <section className="sc-container sc-section sc-split">
        <div>
          <p className="sc-eyebrow">An honest roadmap</p>
          <h2>Know what is ready.</h2>
          <p>
            Try the current single-round club-day foundation in the live demo. Larger event formats
            and paid entries remain planned.
          </p>
        </div>
        <div className="sc-panel sc-roadmap">
          <p>
            <span className="sc-tag">Planned</span> Cable jams and multi-round boat events
          </p>
          <p>
            <span className="sc-tag">Planned</span> Series rankings and Tour
          </p>
          <p>
            <span className="sc-tag">Planned</span> Paid entries and athlete accounts
          </p>
          <p className="sc-secondary">
            Rule packs remain unverified until reviewed by a certified judge. No sanctioned-event
            endorsement is implied.
          </p>
        </div>
      </section>
      <section className="sc-container sc-section">
        <div className="sc-section-heading">
          <p className="sc-eyebrow">Before you start</p>
          <h2>A few practical details.</h2>
        </div>
        <div className="sc-faq">
          <details>
            <summary>What happens if the internet drops?</summary>
            <p>
              Live public screens need connectivity. They retain the last loaded result and show an
              offline message. Offline judging and replay are planned for a later release.
            </p>
          </details>
          <details>
            <summary>Can I take paid entries?</summary>
            <p>
              Paid registration is planned for a future release. It is not available to purchase
              today.
            </p>
          </details>
          <details>
            <summary>Does Scorio support waterski scoring?</summary>
            <p>
              Measured waterski formats are planned groundwork and are not sold as a supported
              scoring format.
            </p>
          </details>
          <details>
            <summary>Can I try it without an account?</summary>
            <p>
              Yes. The read-only demo uses fictional riders and an illustrative score recording
              through the real public results service.
            </p>
          </details>
        </div>
      </section>
      <section className="sc-container sc-final">
        <p className="sc-eyebrow">Your next club day</p>
        <h2>Bring the scores into view.</h2>
        <div className="sc-actions">
          <Link className="sc-button sc-button-primary" to="/login">
            Start free
          </Link>
          <Link className="sc-button" to="/results/demo">
            Try demo
          </Link>
          <a
            className="sc-text-link"
            href="mailto:hello@scorio.live?subject=Tour%20and%20federation%20enquiry"
          >
            Tours &amp; federations: talk to us →
          </a>
        </div>
      </section>
    </PublicLayout>
  );
}
